import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileSpreadsheet, Loader2, AlertTriangle, Info, ArrowLeft, Check } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { apiFetch } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { track } from '@/lib/analytics';
import {
  parseCsv, decodeCsvBytes, guessMapping, guessByContent, looksLikeHeader, buildImportRows, chunk, splitList,
  IMPORT_FIELDS, type ImportField, type ParsedCsv, type MappedRow,
} from '@/lib/csv';

/** Teto por chamada da API (POST /contacts/import). Acima disso, mandamos em lotes. */
const BATCH = 1000;
/** Arquivo maior que isso é planilha errada, não base de contatos. */
const MAX_TEXT_BYTES = 8 * 1024 * 1024;
/** Quantas linhas com observação a prévia lista antes de resumir. */
const ISSUES_SHOWN = 40;

interface RowIssue { index: number; phone?: string; reason: string; detail?: string }
interface ImportResponse {
  dry_run?: boolean;
  total: number; created: number; updated: number; skipped: number; failed: number;
  skipped_rows?: RowIssue[];
  errors?: RowIssue[];
}

/** Observação já traduzida para a linha do ARQUIVO (não o índice do lote). */
interface Issue { line: number; phone?: string; reason: string; detail?: string }

interface Report {
  total: number; created: number; updated: number; skipped: number; failed: number;
  issues: Issue[];
}

const EMPTY: Report = { total: 0, created: 0, updated: 0, skipped: 0, failed: 0, issues: [] };

/** Rótulo do campo no de-para (reaproveita a microcópia do cadastro de contato). */
const FIELD_LABEL: Record<ImportField, string> = {
  phone: 'contacts.phone', lid: 'contacts.lid', bsuid: 'contacts.bsuid', name: 'contacts.name', email: 'contacts.email',
  document: 'contacts.document', document_type: 'contacts.documentType',
  tags: 'contacts.tags', groups: 'contacts.groups',
  street: 'contacts.street', number: 'contacts.number', complement: 'contacts.complement',
  district: 'contacts.district', city: 'contacts.city', state: 'contacts.state',
  zip: 'contacts.zip', country: 'contacts.country',
};

const DELIMITER_LABEL: Record<string, string> = { ',': ',', ';': ';', '\t': 'TAB', '|': '|' };

/**
 * Importação de contatos em 3 passos: ler a planilha → conferir as colunas →
 * ENSAIO (dry_run) e só então gravar.
 *
 * O ensaio não é enfeite: é o que separa "importei minha base" de "estraguei
 * minha base". O servidor devolve linha a linha o que faria, inclusive quem
 * seria pulado por opt-out/bloqueio/supressão — e ninguém ressuscita por
 * planilha.
 */
export function ImportContactsDialog({
  open, onOpenChange, onImported,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onImported: () => void;
}) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<'pick' | 'map' | 'preview' | 'done'>('pick');
  const [parsed, setParsed] = useState<ParsedCsv | null>(null);
  const [hasHeader, setHasHeader] = useState(true);
  const [mapping, setMapping] = useState<(ImportField | null)[]>([]);
  const [dry, setDry] = useState<Report | null>(null);
  const [final, setFinal] = useState<Report | null>(null);
  const [busy, setBusy] = useState<'dry' | 'import' | null>(null);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [dragging, setDragging] = useState(false);
  // Tags e grupos para TODAS as linhas — a lista só de números/LIDs já entra marcada.
  const [allTags, setAllTags] = useState('');
  const [allGroups, setAllGroups] = useState('');
  // Prova do opt-in da lista inteira: onde as pessoas aceitaram e o texto aceito.
  // Com ela os contatos entram ativos (vão para campanha); sem ela, pendentes.
  const [optSource, setOptSource] = useState('');
  const [optText, setOptText] = useState('');
  const optIn = optSource.trim() && optText.trim()
    ? { source: optSource.trim(), text: optText.trim() }
    : null;
  const optInPartial = !optIn && (optSource.trim() !== '' || optText.trim() !== '');

  const reset = () => {
    setStep('pick'); setParsed(null); setHasHeader(true); setMapping([]);
    setDry(null); setFinal(null); setBusy(null); setProgress({ done: 0, total: 0 });
    setAllTags(''); setAllGroups(''); setOptSource(''); setOptText('');
  };

  const close = (o: boolean) => {
    if (busy) return;               // não fecha no meio de uma gravação
    if (!o) reset();
    onOpenChange(o);
  };

  /** Linhas de DADOS (fora o cabeçalho) já mapeadas nos campos da API. */
  const rows: MappedRow[] = useMemo(() => {
    if (!parsed) return [];
    const from = hasHeader ? 1 : 0;
    return buildImportRows(parsed.rows.slice(from), mapping, parsed.lines.slice(from));
  }, [parsed, hasHeader, mapping]);

  const phoneMapped = mapping.includes('phone') || mapping.includes('lid');
  const headers = parsed?.rows[0] ?? [];

  const load = (text: string) => {
    if (text.length > MAX_TEXT_BYTES) { toast.error(t('contacts.importFileTooLarge')); return; }
    const p = parseCsv(text);
    if (p.rows.length === 0) { toast.error(t('contacts.importParseFailed')); return; }
    const header = looksLikeHeader(p.rows[0]);
    setParsed(p);
    setHasHeader(header);
    setMapping(header ? guessMapping(p.rows[0]) : guessByContent(p.rows));
    setStep('map');
  };

  const onFile = async (f?: File | null) => {
    if (!f) return;
    try {
      load(decodeCsvBytes(await f.arrayBuffer()));
    } catch {
      toast.error(t('contacts.importParseFailed'));
    }
  };

  /** Cabeçalho ligado/desligado muda a 1ª linha: o de-para é recalculado. */
  const toggleHeader = (on: boolean) => {
    setHasHeader(on);
    if (!parsed) return;
    setMapping(on ? guessMapping(parsed.rows[0]) : guessByContent(parsed.rows));
  };

  const setColumn = (col: number, value: string) => {
    setMapping((prev) => {
      const next = [...prev];
      const field = value === 'ignore' ? null : (value as ImportField);
      // Um campo só pode vir de uma coluna: se já estava em outra, ela é liberada.
      if (field) next.forEach((f, i) => { if (f === field && i !== col) next[i] = null; });
      next[col] = field;
      return next;
    });
  };

  /** Roda a importação em lotes de 1000, somando o relatório de cada um. */
  const run = async (dryRun: boolean): Promise<Report | null> => {
    const batches = chunk(rows, BATCH);
    const agg: Report = { ...EMPTY, issues: [] };
    setProgress({ done: 0, total: rows.length });
    let sent = 0;
    for (const batch of batches) {
      const r = await apiFetch('POST', '/contacts/import', {
        contacts: batch.map((b) => b.contact),
        dry_run: dryRun,
        ...(splitList(allTags).length ? { tags: splitList(allTags) } : {}),
        ...(splitList(allGroups).length ? { groups: splitList(allGroups) } : {}),
        ...(optIn ? { opt_in: optIn } : {}),
      });
      if (!r.ok) {
        const code = (r.data as { code?: string } | null)?.code;
        toast.error(code === 'import_too_large' ? t('contacts.importFileTooLarge') : t('contacts.importCallFailed'));
        // Lote que quebrou no meio de uma gravação de verdade: os anteriores já
        // entraram, então a lista tem que refletir isso.
        if (!dryRun && sent > 0) onImported();
        return null;
      }
      const d = r.data as ImportResponse;
      agg.total += d.total ?? batch.length;
      agg.created += d.created ?? 0;
      agg.updated += d.updated ?? 0;
      agg.skipped += d.skipped ?? 0;
      agg.failed += d.failed ?? 0;
      // O índice vem relativo ao LOTE: virar linha do arquivo é o que deixa o
      // relatório utilizável (o usuário vai olhar a planilha dele).
      for (const it of [...(d.skipped_rows ?? []), ...(d.errors ?? [])]) {
        agg.issues.push({
          line: batch[it.index]?.line ?? it.index + 1,
          phone: it.phone, reason: it.reason, detail: it.detail,
        });
      }
      sent += batch.length;
      setProgress({ done: sent, total: rows.length });
    }
    agg.issues.sort((a, b) => a.line - b.line);
    return agg;
  };

  const preview = async () => {
    if (!phoneMapped) { toast.error(t('contacts.importPhoneUnmapped')); return; }
    if (rows.length === 0) { toast.error(t('contacts.importNothing')); return; }
    if (optInPartial) { toast.error(t('contacts.importOptInIncomplete')); return; }
    setBusy('dry');
    const r = await run(true);
    setBusy(null);
    if (!r) return;
    setDry(r);
    setStep('preview');
    track('contacts_import_preview', { rows: rows.length });
  };

  const confirm = async () => {
    setBusy('import');
    const r = await run(false);
    setBusy(null);
    if (!r) return;
    setFinal(r);
    setStep('done');
    track('contacts_imported', { created: r.created, updated: r.updated });
    toast.success(t('contacts.importDoneToast', {
      created: r.created, updated: r.updated, skipped: r.skipped, failed: r.failed,
    }));
    onImported();
  };

  const report = final ?? dry;

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="size-5" /> {t('contacts.importTitle')}
          </DialogTitle>
          <DialogDescription>
            {step === 'preview' ? t('contacts.importPreviewDesc')
              : step === 'done' ? t('contacts.importDoneDesc')
              : step === 'map' ? t('contacts.importMapDesc')
              : t('contacts.importDesc')}
          </DialogDescription>
        </DialogHeader>

        {/* 1. arquivo ou texto colado */}
        {step === 'pick' && (
          <div className="flex flex-col gap-4">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); void onFile(e.dataTransfer.files?.[0]); }}
              className={'flex flex-col items-center gap-2 rounded-lg border-2 border-dashed px-4 py-8 text-center transition-colors ' +
                (dragging ? 'border-primary bg-primary/5' : 'border-input hover:bg-accent/50')}
            >
              <Upload className="size-6 text-muted-foreground" />
              <span className="text-sm font-medium">{t('contacts.importDrop')}</span>
              <span className="text-xs text-muted-foreground">{t('contacts.importFormats')}</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.tsv,.txt,text/csv,text/plain"
              className="hidden"
              onChange={(e) => { void onFile(e.target.files?.[0]); e.target.value = ''; }}
            />
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="csv-paste">{t('contacts.importPasteLabel')}</Label>
              <textarea
                id="csv-paste"
                rows={5}
                placeholder={'telefone;nome;e-mail\n+5511999998888;Ana;ana@exemplo.com'}
                onPaste={(e) => {
                  const text = e.clipboardData.getData('text');
                  if (text.trim()) { e.preventDefault(); load(text); }
                }}
                className="w-full rounded-md border border-input bg-transparent p-3 font-mono text-xs shadow-sm outline-none focus-visible:ring-1 focus-visible:ring-ring"
              />
            </div>
            <p className="flex gap-2 rounded-md bg-muted/60 p-3 text-xs text-muted-foreground">
              <Info className="mt-px size-4 shrink-0" /> {t('contacts.importPendingNotice')}
            </p>
          </div>
        )}

        {/* 2. de-para das colunas */}
        {step === 'map' && parsed && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>{t('contacts.importDetected', {
                rows: parsed.rows.length,
                delimiter: DELIMITER_LABEL[parsed.delimiter] ?? parsed.delimiter,
              })}</span>
              <label className="flex cursor-pointer items-center gap-1.5">
                <input type="checkbox" checked={hasHeader} onChange={(e) => toggleHeader(e.target.checked)} className="size-3.5 accent-[var(--primary)]" />
                {t('contacts.importHasHeader')}
              </label>
            </div>

            <div className="max-h-72 overflow-y-auto rounded-md border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-card">
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 font-medium">{t('contacts.importColumn')}</th>
                    <th className="px-3 py-2 font-medium">{t('contacts.importSample')}</th>
                    <th className="px-3 py-2 font-medium">{t('contacts.importField')}</th>
                  </tr>
                </thead>
                <tbody>
                  {(hasHeader ? headers : headers.map((_, i) => t('contacts.importColumnN', { n: i + 1 }))).map((h, col) => (
                    <tr key={col} className="border-b last:border-0">
                      <td className="max-w-[9rem] truncate px-3 py-2 font-medium">{h || t('contacts.importColumnN', { n: col + 1 })}</td>
                      <td className="max-w-[10rem] truncate px-3 py-2 text-xs text-muted-foreground">
                        {parsed.rows[hasHeader ? 1 : 0]?.[col] || '—'}
                      </td>
                      <td className="px-3 py-2">
                        <Select value={mapping[col] ?? 'ignore'} onValueChange={(v) => setColumn(col, v)}>
                          <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="ignore">{t('contacts.importIgnore')}</SelectItem>
                            {IMPORT_FIELDS.map((f) => <SelectItem key={f} value={f}>{t(FIELD_LABEL[f])}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Para TODAS as linhas: a lista só de números/LIDs já entra marcada. */}
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="imp-all-tags" className="text-xs font-medium">{t('contacts.importAllTags')}</label>
                <input id="imp-all-tags" value={allTags} onChange={(e) => setAllTags(e.target.value)}
                  placeholder={t('contacts.importAllPlaceholder')}
                  className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
              </div>
              <div>
                <label htmlFor="imp-all-groups" className="text-xs font-medium">{t('contacts.importAllGroups')}</label>
                <input id="imp-all-groups" value={allGroups} onChange={(e) => setAllGroups(e.target.value)}
                  placeholder={t('contacts.importAllPlaceholder')}
                  className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t('contacts.importPhoneOrLid')}</p>

            {/* Opt-in da lista: é o que libera os contatos para campanha. */}
            <fieldset className="flex flex-col gap-3 rounded-lg border p-3">
              <legend className="px-1 text-xs font-semibold">{t('contacts.importOptInTitle')}</legend>
              <div>
                <label htmlFor="imp-optin-source" className="text-xs font-medium">{t('contacts.importOptInSource')}</label>
                <input id="imp-optin-source" value={optSource} onChange={(e) => setOptSource(e.target.value)}
                  maxLength={200} placeholder={t('contacts.importOptInSourcePlaceholder')}
                  className="mt-1 h-9 w-full rounded-md border border-input bg-background px-3 text-sm" />
              </div>
              <div>
                <label htmlFor="imp-optin-text" className="text-xs font-medium">{t('contacts.importOptInText')}</label>
                <textarea id="imp-optin-text" value={optText} onChange={(e) => setOptText(e.target.value)}
                  maxLength={2000} rows={2} placeholder={t('contacts.importOptInTextPlaceholder')}
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </div>
              <p className="text-xs text-muted-foreground">{t('contacts.importOptInHint')}</p>
            </fieldset>

            {!phoneMapped && (
              <p className="flex items-center gap-2 text-xs text-destructive">
                <AlertTriangle className="size-4" /> {t('contacts.importPhoneUnmapped')}
              </p>
            )}
            <p className="text-xs text-muted-foreground">{t('contacts.importWillValidate', { rows: rows.length })}</p>
          </div>
        )}

        {/* 3. prévia do ensaio / relatório final */}
        {(step === 'preview' || step === 'done') && report && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Counter label={t('contacts.importCreated')} value={report.created} tone="success" />
              <Counter label={t('contacts.importUpdated')} value={report.updated} />
              <Counter label={t('contacts.importSkipped')} value={report.skipped} tone={report.skipped ? 'warning' : undefined} />
              <Counter label={t('contacts.importErrors')} value={report.failed} tone={report.failed ? 'destructive' : undefined} />
            </div>

            {report.issues.length > 0 && (
              <div className="flex flex-col gap-1.5">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{t('contacts.importIssues')}</p>
                <ul className="max-h-48 divide-y overflow-y-auto rounded-md border text-sm">
                  {report.issues.slice(0, ISSUES_SHOWN).map((it, i) => (
                    <li key={i} className="flex items-baseline justify-between gap-3 px-3 py-1.5">
                      <span className="min-w-0 truncate">
                        <span className="text-xs text-muted-foreground">{t('contacts.importRowLabel', { row: it.line })}</span>{' '}
                        {it.phone || '—'}
                      </span>
                      <span className="shrink-0 text-xs text-muted-foreground" title={it.detail}>{reasonLabel(t, it.reason)}</span>
                    </li>
                  ))}
                </ul>
                {report.issues.length > ISSUES_SHOWN && (
                  <p className="text-xs text-muted-foreground">{t('contacts.importAndMore', { count: report.issues.length - ISSUES_SHOWN })}</p>
                )}
              </div>
            )}

            {step === 'preview' && (
              <p className="flex gap-2 rounded-md bg-muted/60 p-3 text-xs text-muted-foreground">
                <Info className="mt-px size-4 shrink-0" /> {t('contacts.importPendingNotice')}
              </p>
            )}
            {busy === 'import' && (
              <p className="text-xs text-muted-foreground">{t('contacts.importProgress', { done: progress.done, total: progress.total })}</p>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'map' && (
            <>
              <Button variant="ghost" onClick={() => reset()} disabled={!!busy}>
                <ArrowLeft className="size-4" /> {t('contacts.importBack')}
              </Button>
              <Button onClick={preview} disabled={!phoneMapped || !!busy || rows.length === 0}>
                {busy === 'dry' ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />}
                {busy === 'dry' ? t('contacts.importValidating') : t('contacts.importPreview')}
              </Button>
            </>
          )}
          {step === 'preview' && (
            <>
              <Button variant="ghost" onClick={() => setStep('map')} disabled={!!busy}>
                <ArrowLeft className="size-4" /> {t('contacts.importBack')}
              </Button>
              <Button onClick={confirm} disabled={!!busy || (report?.created ?? 0) + (report?.updated ?? 0) === 0}>
                {busy === 'import' && <Loader2 className="size-4 animate-spin" />}
                {t('contacts.importConfirm', { count: (report?.created ?? 0) + (report?.updated ?? 0) })}
              </Button>
            </>
          )}
          {step === 'done' && <Button onClick={() => close(false)}>{t('common.ready')}</Button>}
          {step === 'pick' && <Button variant="ghost" onClick={() => close(false)}>{t('common.cancel')}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Counter({ label, value, tone }: { label: string; value: number; tone?: 'success' | 'warning' | 'destructive' }) {
  const color = tone === 'success' ? 'text-[#3c9a18]'
    : tone === 'warning' ? 'text-amber-600 dark:text-amber-500'
    : tone === 'destructive' ? 'text-destructive'
    : '';
  return (
    <div className="rounded-md border p-3">
      <p className={'text-xl font-semibold ' + color}>{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
    </div>
  );
}

/**
 * reasonLabel traduz o motivo NEUTRO da API. Código novo no servidor (ou
 * catálogo sem a chave) cai no próprio código em vez de virar texto quebrado.
 */
function reasonLabel(t: (k: string) => string, reason: string): string {
  const key = `contacts.reason.${reason}`;
  const out = t(key);
  return out.endsWith(key) ? reason : out;
}
